"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Eye, TrendingUp, Trophy } from "lucide-react";

const PROMOTERS = [
  { name: "Sofia Reyes", initial: "S", tickets: 87, revenue: 3480, comm: 522, top: true, hue: 320 },
  { name: "Marcus Vega", initial: "M", tickets: 64, revenue: 2560, comm: 384, hue: 200 },
  { name: "Jasmine Khan", initial: "J", tickets: 52, revenue: 2080, comm: 312, hue: 30 },
  { name: "Diego Torres", initial: "D", tickets: 41, revenue: 1640, comm: 246, hue: 280 },
];

export default function PromoterDashboardScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.15) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Manager view", "Vista del manager")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Leaderboard. ", "Leaderboard. ")}
            <span className="text-white/30">{t("In real time.", "En tiempo real.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "You see all your promoters at once, ranked — tickets, revenue and commission side by side. Decide who keeps their spot for next week's event before they're done complaining.",
              "Ves todos tus promotores a la vez, en ranking — tickets, revenue y comisión lado a lado. Decide quién mantiene su spot para el siguiente evento antes de que terminen de quejarse."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[28px] p-6 md:p-8 max-w-[920px] mx-auto"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-white/55" />
              <div className="text-[10px] font-black tracking-widest uppercase text-white/55">
                {t("All promoters · NEON RUSH", "Todos los promotores · NEON RUSH")}
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <TrendingUp className="w-3.5 h-3.5" style={{ color: "#F472B6" }} />
              <span className="text-[11px] font-bold" style={{ color: "#F472B6" }}>
                {t("12 active", "12 activos")}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            {PROMOTERS.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.45, delay: 0.1 + 0.08 * i }}
                className={`rounded-[14px] p-3.5 border flex items-center gap-4 ${
                  p.top ? "" : "bg-white/[0.03] border-white/5"
                }`}
                style={
                  p.top
                    ? {
                        backgroundColor: "rgba(244,114,182,0.10)",
                        borderColor: "rgba(244,114,182,0.30)",
                      }
                    : undefined
                }
              >
                <div className="text-[14px] font-black text-white/35 w-5 tabular-nums">#{i + 1}</div>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-[13px] shrink-0"
                  style={{
                    background: p.top
                      ? "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)"
                      : `hsl(${p.hue}, 60%, 55%)`,
                  }}
                >
                  {p.initial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-black text-white truncate">{p.name}</div>
                    {p.top && (
                      <Trophy className="w-3.5 h-3.5" style={{ color: "#F472B6" }} fill="currentColor" />
                    )}
                  </div>
                  <div className="text-[10px] text-white/45">
                    {p.tickets} {t("tickets sold", "tickets vendidos")} · ${p.revenue.toLocaleString()} {t("revenue", "revenue")}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[14px] font-black text-white tabular-nums">${p.comm}</div>
                  <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: p.top ? "#F472B6" : "rgba(255,255,255,0.45)" }}>
                    {t("Commission", "Comisión")}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
